import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import {
  Star, ShieldCheck, Briefcase, CheckCircle, XCircle, ArrowsLeftRight, Plus, X, Users
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SupplierComparePage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState([]);
  const [addValue, setAddValue] = useState("");
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get("/suppliers").then(({ data }) => setVendors(data)).catch(() => {});
  }, []);

  const handleAdd = () => {
    if (!addValue || selected.includes(addValue)) return;
    if (selected.length >= 3) { toast.error("Maximum 3 suppliers"); return; }
    setSelected(prev => [...prev, addValue]);
    setAddValue("");
  };

  const handleRemove = (id) => {
    setSelected(prev => prev.filter(v => v !== id));
    setComparison(null);
  };

  const handleCompare = async () => {
    if (selected.length < 2) { toast.error("Select at least 2 suppliers"); return; }
    setLoading(true);
    try {
      const { data } = await API.post("/suppliers/compare", { vendor_ids: selected });
      setComparison(data.suppliers);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to compare"); }
    finally { setLoading(false); }
  };

  const RATING_KEYS = ["quality", "reliability", "communication", "pricing", "overall"];
  const REQUIRED_DOCS = ["insurance", "public_liability", "risk_assessment", "method_statement"];
  const availableVendors = vendors.filter(v => !selected.includes(v.id));

  return (
    <div data-testid="supplier-compare-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Compare Suppliers</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Select 2-3 suppliers to compare side-by-side</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/vendors")} data-testid="back-to-suppliers">
          <Users size={14} className="mr-1" /> Back to Directory
        </Button>
      </div>

      {/* Selection Bar */}
      <div className="border border-border rounded-lg p-4 bg-card mb-6" data-testid="compare-selection">
        <div className="flex items-center gap-3 flex-wrap">
          {selected.map(id => {
            const v = vendors.find(x => x.id === id);
            return (
              <div key={id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                <span className="text-xs font-semibold">{v?.company_name || id}</span>
                <button onClick={() => handleRemove(id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-${id}`}>
                  <X size={12} />
                </button>
              </div>
            );
          })}
          {selected.length < 3 && (
            <div className="flex items-center gap-2">
              <Select value={addValue} onValueChange={setAddValue}>
                <SelectTrigger className="w-48 h-8 text-xs" data-testid="compare-add-select"><SelectValue placeholder="Add supplier..." /></SelectTrigger>
                <SelectContent>
                  {availableVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-8" onClick={handleAdd} disabled={!addValue} data-testid="compare-add-btn">
                <Plus size={14} />
              </Button>
            </div>
          )}
          {selected.length >= 2 && (
            <Button size="sm" className="ml-auto" onClick={handleCompare} disabled={loading} data-testid="compare-run-btn">
              <ArrowsLeftRight size={14} className="mr-1" /> {loading ? "Comparing..." : "Compare"}
            </Button>
          )}
        </div>
      </div>

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-6" data-testid="compare-results">
          {/* Overview */}
          <Section title="Overview">
            <CompareRow label="Company" values={comparison.map(c => c.company_name)} />
            <CompareRow label="Contact" values={comparison.map(c => c.contact_name || "-")} />
            <CompareRow label="Email" values={comparison.map(c => c.email || "-")} />
            <CompareRow label="Phone" values={comparison.map(c => c.phone || "-")} />
            <CompareRow label="Trade" values={comparison.map(c => c.trade || "-")} />
            <CompareRow label="Status" values={comparison.map(c => (
              <Badge variant={c.status === "active" ? "default" : "secondary"} className="text-[10px]">{c.status}</Badge>
            ))} />
            <CompareRow label="Sectors" values={comparison.map(c => (
              c.sectors?.length > 0 ? <div className="flex flex-wrap gap-1">{c.sectors.map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}</div> : <span className="text-muted-foreground">-</span>
            ))} />
            <CompareRow label="Areas" values={comparison.map(c => (
              c.areas_covered?.length > 0 ? <div className="flex flex-wrap gap-1">{c.areas_covered.map(a => <Badge key={a} variant="outline" className="text-[10px]">{a}</Badge>)}</div> : <span className="text-muted-foreground">-</span>
            ))} />
          </Section>

          {/* Ratings */}
          <Section title="Ratings">
            <CompareRow label="Overall" values={comparison.map(c => (
              <div className="flex items-center gap-1.5">
                <Star size={14} weight="fill" className="text-amber-400" />
                <span className="font-bold">{c.average_rating || "-"}</span>
                <span className="text-[10px] text-muted-foreground">({c.rating_count} reviews)</span>
              </div>
            ))} highlight />
            {RATING_KEYS.filter(k => k !== "overall").map(key => {
              const vals = comparison.map(c => c.rating_averages?.[key] || 0);
              const best = Math.max(...vals);
              return (
                <CompareRow key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} values={comparison.map(c => {
                  const v = c.rating_averages?.[key] || 0;
                  return (
                    <div className="flex items-center gap-2">
                      <Progress value={(v / 5) * 100} className="h-1.5 flex-1" />
                      <span className={`text-xs font-semibold ${v === best && v > 0 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>{v || "-"}</span>
                    </div>
                  );
                })} />
              );
            })}
          </Section>

          {/* Performance */}
          <Section title="Performance">
            <CompareRow label="Tasks Assigned" values={comparison.map(c => {
              const best = Math.max(...comparison.map(x => x.task_count));
              return <span className={`font-semibold ${c.task_count === best && best > 0 ? "text-primary" : ""}`}>{c.task_count}</span>;
            })} />
            <CompareRow label="Tasks Completed" values={comparison.map(c => {
              const best = Math.max(...comparison.map(x => x.completed_count));
              return <span className={`font-semibold ${c.completed_count === best && best > 0 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>{c.completed_count}</span>;
            })} />
            <CompareRow label="Completion Rate" values={comparison.map(c => {
              const rate = c.task_count > 0 ? Math.round((c.completed_count / c.task_count) * 100) : 0;
              return (
                <div className="flex items-center gap-2">
                  <Progress value={rate} className="h-1.5 flex-1" />
                  <span className="text-xs font-semibold">{rate}%</span>
                </div>
              );
            })} />
          </Section>

          {/* Compliance */}
          <Section title="Compliance">
            <CompareRow label="Total Documents" values={comparison.map(c => c.total_documents)} />
            <CompareRow label="Missing Docs" values={comparison.map(c => (
              c.missing_documents.length === 0 ? (
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold text-xs">All uploaded</span>
              ) : (
                <span className="text-red-600 dark:text-red-400 font-semibold text-xs">{c.missing_documents.length} missing</span>
              )
            ))} highlight />
            {REQUIRED_DOCS.map(dt => (
              <CompareRow key={dt} label={dt.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} values={comparison.map(c => (
                c.compliance_docs_present.includes(dt) ? (
                  <CheckCircle size={16} className="text-emerald-500" weight="fill" />
                ) : (
                  <XCircle size={16} className="text-red-400" weight="fill" />
                )
              ))} />
            ))}
          </Section>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            {comparison.map(c => (
              <Button key={c.supplier_id} variant="outline" className="flex-1" onClick={() => navigate(`/vendors/${c.supplier_id}`)} data-testid={`view-${c.supplier_id}`}>
                View {c.company_name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {!comparison && selected.length < 2 && (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <ArrowsLeftRight size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Select at least 2 suppliers to start comparing</p>
          <p className="text-[10px] text-muted-foreground mt-1">You can compare up to 3 suppliers side-by-side</p>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid={`section-${title.toLowerCase()}`}>
      <div className="px-4 py-2.5 bg-muted/50 border-b border-border">
        <h3 className="text-xs font-semibold font-[Chivo] uppercase tracking-wider">{title}</h3>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

function CompareRow({ label, values, highlight }) {
  return (
    <div className={`flex items-center ${highlight ? "bg-muted/30" : ""}`}>
      <div className="w-36 shrink-0 px-4 py-2.5">
        <span className="text-[11px] text-muted-foreground font-medium">{label}</span>
      </div>
      {values.map((v, i) => (
        <div key={i} className="flex-1 px-4 py-2.5 text-xs border-l border-border">{v}</div>
      ))}
    </div>
  );
}
